/**
 * 发薪日工作流契约 —— **F 域 F3**（`F域PRD` §4.7 · `AC-F3.1`–`F3.16` · `DR-052` · 批 87）
 *
 * ## 🔴 本文件只定形状；判定全在 `nucleus/payday.ts`
 *
 * | 判定 | 实现处 |
 * |---|---|
 * | 这算不算工资 / 可分配多少 | `recognizeSalary()` |
 * | 每个目标该攒多少 | `autoSaveAmountOf()` |
 * | 「本次分配后可花」 | `composePayday()`（**只此一处**，`AC-F3.9`） |
 *
 * ## 🔴 两个名字必须分开（`AC-F3.9` 必测）
 *
 * | 字段 | 口径 | 归属 |
 * |---|---|---|
 * | `distributableCents` | **本次分配后可花** | **F3**（本文件） |
 * | （J 域）`spendable` | 本月剩余可花 | J 域，**不在此** |
 *
 * 端侧**不得**用一个字段去渲染另一个的文案。
 */

import { z } from 'zod';
import type { Cents } from '../types/brand.js';
import type { AutoSaveMode } from '../enums/plan.js';
import { DayString } from './pagination.js';
import { PaydayPolicy } from '../constants/index.js';

// ═══════════════════════ ① 候选（入口：未分配的工资） ═══════════════════════

/** 一笔"还没分配过"的工资（`AC-F3.11` 入口常驻的来源） */
export interface PaydayCandidateView {
  readonly transactionId: string;
  /** 工资到账日（账本本地日历日） */
  readonly payday: string;
  /** 实发（分组净流入）—— `recognizeSalary().netCents` */
  readonly netCents: Cents;
  readonly currency: string;
  /**
   * 摘要 = **交易备注原文**；`null` = 没写备注。
   *
   * 🔴 默认文案（「工资」）归**端侧**：分类名的中文字面量在 server/shared 被 ESLint 禁止
   * （规格 §22.4），服务端不该为了一个占位词去引用分类名。
   */
  readonly summary: string | null;
}

export interface PaydayCandidateListResult {
  readonly items: readonly PaydayCandidateView[];
  /** 账本本币（金额展示用） */
  readonly currency: string;
}

// ═══════════════════════ ② 预演（**只读**） ═══════════════════════

/**
 * I 域提供给 F3 的**只读取数面**（`GoalService.listForPayday()` 的返回）。
 *
 * 🔴 为什么这个类型放在 F3 的契约里而不是 `goal.ts`：它是**消费方定义的输入需求**
 * （F3 需要"目标 + 剩余额 + 自动攒配置 + 能否分配"），I 域只是提供者。
 * 放在消费方一侧，I 域新增字段时不会"顺手"改变 F3 的输入形状。
 */
export interface PaydayGoalSource {
  readonly goalId: string;
  readonly name: string;
  readonly targetCents: Cents;
  readonly savedCents: Cents;
  /** `target − saved`（可为负 = 已超额） */
  readonly remainingCents: Cents;
  readonly autoSaveMode: AutoSaveMode;
  readonly autoSaveValue: number;
  /** 不可分配的原因（`null` = 可分配）—— **由 I 域判**（它才知道账户/币种/达成状态） */
  readonly blockedReason: string | null;
}

/** 预演里的一项目标 */
export interface PaydayGoalPlanView {
  readonly goalId: string;
  readonly name: string;
  readonly targetCents: Cents;
  readonly savedCents: Cents;
  /** 目标还差多少（`target − saved`，可为负 = 已超额） */
  readonly remainingCents: Cents;
  /** 该目标的自动攒配置（原样回显，端侧可显示"按 10%"） */
  readonly autoSaveMode: AutoSaveMode;
  readonly autoSaveValue: number;
  /**
   * 本期建议攒多少（`autoSaveAmountOf()` 的结果，**服务端算好**）。
   * 🔴 端侧**不得**自己按比例算一遍（`AC-P1.2` 在端侧封死了除法）。
   */
  readonly plannedCents: Cents;
  /**
   * 不可分配的原因（`null` = 可分配）。
   *
   * 例：目标未绑账户（`DR-050` D3：首次攒要选宿主）、宿主币种 ≠ 账本本币、
   * 已达成的目标（`remaining ≤ 0`）。
   */
  readonly blockedReason: string | null;
}

/** 必付项的一行（预演里给用户看"钱被什么占住了"） */
export interface PaydayPayableLineView {
  readonly label: string;
  /** `creditCard` / `payable` / `schedule`（与 F2 的 `UpcomingKind` 同值） */
  readonly kind: string;
  readonly dueDate: string;
  readonly amountCents: Cents;
}

export interface PaydayPreviewView {
  readonly transactionId: string;
  readonly payday: string;
  readonly currency: string;
  /** 工资实发（可分配基数） */
  readonly netCents: Cents;
  /** 本月必付合计（来自 F2 唯一聚合，**F3 不算**） */
  readonly payableCents: Cents;
  readonly payableLines: readonly PaydayPayableLineView[];
  /** 必付合计是否不完整（缺汇率）—— UI 须标注，**不得当 0 处理** */
  readonly payableIncomplete: boolean;
  readonly goals: readonly PaydayGoalPlanView[];
  /** 勾选项合计（未传 `allocations` 时 = 全部可用目标的建议之和） */
  readonly plannedTotalCents: Cents;
  /** 🔴 `AC-F3.8`：本次分配后可花（**可为负**） */
  readonly distributableCents: Cents;
  readonly overCommitted: boolean;
  /** 提示句（只提示不拦截） */
  readonly issues: readonly string[];
}

/** 一项分配（目标 → 金额） */
export const PaydayAllocationSchema = z.object({
  goalId: z.string().trim().min(1, '目标 id 不得为空'),
  /** 用户可改金额（`AC-F3.4`）；`0` = 这次不给它攒 */
  amountCents: z.number().int('金额必须是整数分').nonnegative('金额不得为负'),
});
export type PaydayAllocationInput = z.infer<typeof PaydayAllocationSchema>;

/**
 * 预演请求（`POST /api/paydays/:transactionId/preview`，**只读**）。
 *
 * 🔴 **预演也接受 `allocations`**：`AC-F3.4` 是"预演 → 逐项勾选/改额 → 再算"，
 * 若改完看不到新的合计，用户只能端侧自算（违反 `AC-P1.2`）或盲提交（铁律 94）。
 */
export const PaydayPreviewRequestSchema = z.object({
  allocations: z.array(PaydayAllocationSchema).max(PaydayPolicy.MaxAllocations).optional(),
});
export type PaydayPreviewRequest = z.infer<typeof PaydayPreviewRequestSchema>;

// ═══════════════════════ ③ 落账 ═══════════════════════

/**
 * 落账请求（`POST /api/paydays/:transactionId/confirm`）。
 *
 * 🔴 **至少勾一项**：全都不勾 = 「这次不分配」⇒ 用 `dismiss` 语义（不建任何行，
 * `AC-F3.12`），而不是发一个空 `allocations` 让服务端去猜。
 */
export const PaydayConfirmRequestSchema = z.object({
  allocations: z
    .array(PaydayAllocationSchema)
    .min(1, '至少勾选一项要分配的目标；若这次不分配，直接关闭即可（不会产生记录）')
    .max(PaydayPolicy.MaxAllocations),
});
export type PaydayConfirmRequest = z.infer<typeof PaydayConfirmRequestSchema>;

/** 一行的落账结果（**失败也在这里**，`AC-F3.7` 可见不静默） */
export interface PaydayAllocationResultView {
  readonly goalId: string;
  readonly goalName: string;
  readonly amountCents: Cents;
  /** `confirmed` = 已落账（含幂等命中）；`failed` = 未落账且**有原因** */
  readonly status: 'confirmed' | 'failed';
  /** 失败原因（人话；成功为 `null`）—— 🔴 端侧按 `status` 分支，**不匹配这个文案** */
  readonly failReason: string | null;
}

export interface PaydayConfirmResultView {
  readonly transactionId: string;
  /** 本次新建的分配行数（幂等命中不算） */
  readonly created: number;
  readonly failed: number;
  /** 幂等跳过（同目标在这一笔工资下已有记录） */
  readonly skipped: number;
  readonly rows: readonly PaydayAllocationResultView[];
  /** 落账后重算的「本次分配后可花」（**服务端算**，端侧不重算） */
  readonly distributableCents: Cents;
}

// ═══════════════════════ ④ 历史（只读） ═══════════════════════

/** 一笔工资下的分配记录（时间倒序） */
export interface PaydayAllocationView {
  readonly id: string;
  readonly payday: string;
  readonly goalId: string;
  readonly goalName: string | null;
  readonly plannedCents: Cents;
  readonly actualCents: Cents;
  readonly status: 'confirmed' | 'failed';
  readonly failReason: string | null;
  readonly createdAt: number;
}

export interface PaydayHistoryResult {
  readonly currency: string;
  readonly items: readonly PaydayAllocationView[];
}

/** `GET /api/paydays/history?from=&to=` 的查询（缺省 = 近 90 天） */
export const PaydayHistoryQuerySchema = z.object({
  from: DayString.optional(),
  to: DayString.optional(),
});
export type PaydayHistoryQuery = z.infer<typeof PaydayHistoryQuerySchema>;
